import { call, cancel, delay, put, race, select, take, takeLatest } from 'redux-saga/effects';

import {
    BillingEvents,
    authRoutines,
    authorizeRequest,
    getForbiddedTenantUrl,
    getIsCloudPortal,
    getPureTenantUrl,
    getRefreshDelayForToken,
    getTenant,
    history,
    isForbiddenTenantReached,
    isTokenJtiValid,
    redirectToSSO,
    refreshTokenRequest,
    retryFetch,
    routes,
    selectDecodedSsoToken,
    selectDecodedToken,
    selectIsOwnerAccount,
    selectIsPendingOwner,
    selectMultiTenants,
} from '@cloud/shared';
import { Cookies } from 'ovpn-ui-toolkit';

function deleteGenericToken() {
    Cookies.setCookie('cvpn_token', '', { 'max-age': -1 });
}

function* redirectToSSOSaga(path, forceTenant) {
    let ssoPath = path;

    if (path === 'login' && window.location.hash?.includes('user')) {
        ssoPath = 'domain/login';
    }

    const url = redirectToSSO(window.location.href, ssoPath, '', forceTenant);

    /**
     * @CRUTCH
     * Added as a fix for https://openvpn.atlassian.net/browse/CGRID-5145
     * Assign to location.href just completely ignored in some unknown scenario.
     * I've no idea why.
     */
    setTimeout(() => {
        window.location.href = url;
    }, 0);

    yield delay(2000); // wait for browser redirect hack
}

function* refreshToken(shouldLogout) {
    try {
        yield call(refreshTokenRequest);
        yield put(authRoutines.refreshToken.success());
    } catch (error) {
        if (process.env.NODE_ENV === 'production') {
            console.error(error, error.message);
        }

        if (shouldLogout) {
            yield call(redirectToSSOSaga, 'logout');
        }
    }
}

function* unauthorizedSaga() {
    yield call(redirectToSSOSaga, 'logout');
}

function* getProductTokenSaga() {
    yield put(authRoutines.getProductToken.request());

    try {
        yield call(retryFetch, authorizeRequest, { exponential: true, times: 9, delay: 50 });
        yield put(authRoutines.getProductToken.success());
        yield put(authRoutines.getProductToken.fulfill());
    } catch (error) {
        if (process.env.NODE_ENV === 'production') {
            console.error(error, error.message);
        }

        yield put(authRoutines.getProductToken.failure(null));
        yield put(authRoutines.getProductToken.fulfill(null));
    }
}

function* validateOwnerTokens() {
    const urlTenant = getTenant();
    const isCloudPortal = urlTenant === 'cloud';

    const ssoTenants = yield select(selectMultiTenants);
    const isPendingOwner = yield select(selectIsPendingOwner);

    // First, if owner is not enrolled - go to sso enrollment
    if (isPendingOwner) {
        yield call(redirectToSSOSaga, 'domain-enrollment');
        return;
    }

    // Show tenant selection screen with warning
    // if owner tries to visit tenant not present in sso token
    if (!isCloudPortal && !ssoTenants.includes(urlTenant)) {
        window.location.href = getForbiddedTenantUrl();

        yield cancel();
    }

    // Redirect to tenant selection screen, if we have more than 1 tenant
    if (isCloudPortal) {
        const path = `${window.location.pathname}${window.location.search}`;

        if (ssoTenants.length === 1 && !isForbiddenTenantReached()) {
            window.location.href = `${getPureTenantUrl(ssoTenants[0])}${path}`;
        } else if (window.location.pathname !== routes.tenants) {
            const shouldAddReturn = path !== '' && path !== '/';

            yield call(history.replace, {
                pathname: routes.tenants,
                search: shouldAddReturn ? `?return=${encodeURIComponent(path)}` : undefined,
            });
        }
        yield cancel();
    }
}

function* validateMemberTokens() {
    const ssoToken = yield select(selectDecodedSsoToken);
    const urlTenant = getTenant();
    const isCloudPortal = urlTenant === 'cloud';
    const ssoTenant = ssoToken?.client_domain;

    if (!ssoTenant) {
        yield call(redirectToSSOSaga, 'logout', 'cloud');
        yield cancel();
    }

    // Push member to tenant page
    if (isCloudPortal) {
        window.location.href = `${getPureTenantUrl(ssoTenant)}${window.location.pathname}${
            window.location.search
        }`;
        yield cancel();
    }

    // Logout member if he goes to unknown tenant url
    if (!isCloudPortal && urlTenant !== ssoTenant) {
        yield call(redirectToSSOSaga, 'logout', ssoTenant);
        yield cancel();
    }
}

function* validateTokenJti(force) {
    yield put(authRoutines.setAuthToken());

    const ssoToken = yield select(selectDecodedSsoToken);
    const token = yield select(selectDecodedToken);

    if (!!force || !isTokenJtiValid(ssoToken, token)) {
        yield put(authRoutines.getProductToken({ tenant: getTenant() }));
    }
}

export default function* () {
    yield takeLatest(authRoutines.getProductToken, getProductTokenSaga);
    yield takeLatest(authRoutines.unauthorized, unauthorizedSaga);

    // there are some actions on other MFE that cause token change
    // so we must react to it and reissue our product token:
    //
    // - action on billing MFE
    yield takeLatest(BillingEvents.BILLING_DOMAIN_CREATE, validateTokenJti);
    // - action on SSO mfe
    yield takeLatest('AUTH/PRODUCT_TOKEN_INVALIDATED/TRIGGER', validateTokenJti, true);

    while (true) {
        deleteGenericToken();
        yield put(authRoutines.setAuthToken());

        let ssoToken = yield select(selectDecodedSsoToken);
        let token = yield select(selectDecodedToken);

        if (!ssoToken) {
            yield call(redirectToSSOSaga, 'login');
            yield cancel();
        }

        const isOwner = yield select(selectIsOwnerAccount);
        yield call(isOwner ? validateOwnerTokens : validateMemberTokens);

        if (
            ssoToken &&
            !isTokenJtiValid(ssoToken, token) &&
            !getIsCloudPortal(window.location.href)
        ) {
            yield put(authRoutines.getProductToken({ tenant: getTenant() }));

            const [tokenUpdate] = yield race([
                take(authRoutines.setAuthToken.TRIGGER),
                take(authRoutines.getProductToken.FAILURE),
            ]);

            if (tokenUpdate) {
                ssoToken = yield select(selectDecodedSsoToken);
                token = yield select(selectDecodedToken);
                yield put(authRoutines.authorized.success());
            } else {
                yield call(redirectToSSOSaga, 'logout');
                yield cancel();
            }
        }

        yield call(isOwner ? validateOwnerTokens : validateMemberTokens);

        while (ssoToken && token) {
            const { refreshDelay, shouldLogout } = getRefreshDelayForToken(token.exp);

            const [expired] = yield race([
                delay(refreshDelay),
                take(authRoutines.setAuthToken.TRIGGER),
            ]);

            if (expired) {
                yield call(refreshToken, shouldLogout);
            }

            ssoToken = yield select(selectDecodedSsoToken);
            token = yield select(selectDecodedToken);

            if (!isTokenJtiValid(ssoToken, token)) {
                break;
            }
        }
    }
}
